import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { FUNCTIONALITY } from '../../../constants/types';

/**
 * SKIN-577 — a visit flagged for correction, as the API serves it.
 *
 * The flag is the API's: an append-only log of steps on one visit, each row a full snapshot
 * (sherwood-api `docs/features/quality-check/correction-flags.md`). This module only names its
 * shapes and answers the questions the reviewer screens ask of it, so the queue, the rail and
 * the banner cannot each decide differently what "open" means.
 */

/** OPEN: waiting for the correction. CORRECTED: corrected, waiting for the reviewer. */
export type CorrectionFlagState = 'OPEN' | 'CORRECTED' | 'RESOLVED';

export type CorrectionFlagAction = 'OPEN' | 'CORRECTED' | 'RESOLVE' | 'REOPEN' | 'REASSIGN';

export interface CorrectionFlagPerson {
    uuid: string;
    name: string | null;
    surnames: string | null;
}

/** One step of the flag's history: who took it, when, and the flag as it stood after it. */
export interface CorrectionFlagEvent {
    action: CorrectionFlagAction;
    state: CorrectionFlagState;
    /** `SurveyField` ids of the QC Survey's VISIT section. */
    reasonFieldIds: number[];
    note: string | null;
    actor: CorrectionFlagPerson | null;
    assignee: CorrectionFlagPerson | null;
    at: string;
}

/** The flag block of the QC visit read. `current` is null when the visit was never flagged. */
export interface CorrectionFlagView {
    current: {
        state: CorrectionFlagState;
        reasonFieldIds: number[];
        assignee: CorrectionFlagPerson | null;
        at: string;
    } | null;
    /** Every step, oldest first. */
    history: CorrectionFlagEvent[];
}

/** Whether a response carries a flag view at all: a missing one is an API older than the client. */
export const isFlagView = (value: unknown): value is CorrectionFlagView => (
    !!value && typeof value === 'object'
    && 'current' in value
    && Array.isArray((value as CorrectionFlagView).history)
);

/**
 * The visit reason "Other (see note)": the only reason a flag must explain in a note.
 *
 * Known by its field name, which carries its catalogue id (sherwood-api
 * `qcFieldName(QC_OTHER_VISIT_REASON_ID)` in `qc-feedback/categories.ts`), never by its label —
 * a study may relabel its reasons — nor by its field id, which differs per study.
 */
export const OTHER_VISIT_REASON_FIELD_NAME = 'qc_cat_33';

/** Whether the picked reasons include "Other", read from the sections they were picked in. */
export function includesOtherReason(
    sections: ReadonlyArray<{ fields: ReadonlyArray<{ id: number; name: string }> }>,
    pickedIds: ReadonlyArray<number>,
): boolean {
    return sections.some((section) => section.fields.some(
        (field) => field.name === OTHER_VISIT_REASON_FIELD_NAME && pickedIds.includes(field.id),
    ));
}

/** The steps a QC reviewer takes from this screen. CORRECTED is the assignee's (My corrections, SKIN-600); REASSIGN is SUPER_ADMIN's. */
export type ReviewerFlagStep = 'OPEN' | 'RESOLVE' | 'REOPEN';

/** Every step the web posts: the reviewer's, and the assignee's CORRECTED (SKIN-600). */
export type FlagStep = ReviewerFlagStep | 'CORRECTED';

/**
 * Whether the flag freezes the visit. CORRECTED is still open: the reviewer has not accepted
 * the correction yet, and the API refuses approval in both states.
 */
export function isFlagOpen(state: CorrectionFlagState | null | undefined): boolean {
    return state === 'OPEN' || state === 'CORRECTED';
}

/**
 * Which reviewer steps the flag's current state allows, in the order the rail offers them.
 *
 * The API's transition map (`CORRECTION_FLAG_TRANSITIONS`), restricted to the reviewer's
 * steps. Offering only what the server will accept means a reviewer never meets
 * `409 INVALID_TRANSITION` from a button this screen showed them.
 */
export function reviewerStepsFor(state: CorrectionFlagState | null): ReviewerFlagStep[] {
    switch (state) {
        case null: return ['OPEN'];
        case 'OPEN': return ['RESOLVE'];
        case 'CORRECTED': return ['RESOLVE', 'REOPEN'];
        case 'RESOLVED': return ['REOPEN', 'OPEN'];
    }
}

/**
 * The step that explains the flag as it stands: the latest OPEN or REOPEN. Its note is the
 * reviewer's reason for sending the visit back, which is what the banner leads with — a
 * later resolve or reassign note answers a different question.
 */
export function latestFlaggingEvent(view: CorrectionFlagView): CorrectionFlagEvent | null {
    for (let i = view.history.length - 1; i >= 0; i -= 1) {
        const event = view.history[i];
        if (event.action === 'OPEN' || event.action === 'REOPEN') return event;
    }
    return null;
}

/** A person as the audit lines name them; null when the API could not resolve them. */
export function personName(person: CorrectionFlagPerson | null): string | null {
    if (!person) return null;
    return [person.name, person.surnames].filter(Boolean).join(' ').trim() || null;
}

interface InvestigationGate {
    permissions?: string[];
    functionalities?: string[];
}

/**
 * The correction flag's gate, on top of the Quality Check one (APPROVE_VISIT + CLINICAL): the
 * study's own `WEB_CLINICAL_CORRECTION` switch. The API refuses every step without both; this
 * hides what would be refused.
 */
export function canUseCorrectionFlag(investigation: InvestigationGate | undefined): boolean {
    return !!investigation?.permissions?.includes(PERMISSION.APPROVE_VISIT)
        && !!investigation?.functionalities?.includes(FUNCTIONALITY.WEB_CLINICAL_CORRECTION);
}
